import CloseIcon from '@mui/icons-material/Close';
import {Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton} from '@mui/material';
import moment from 'moment-timezone';
import React, {useState} from 'react';
import ReactDOM from 'react-dom';
import {FormProvider, useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import layoutActions from 'src/modules/layout/layoutActions';
import Errors from 'src/modules/shared/error/errors';
import tareaListActions from 'src/modules/tarea/list/tareaListActions';
import tareaListSelectors from 'src/modules/tarea/list/tareaListSelectors';
import TareaService from 'src/modules/tarea/tareaService';
import {DatePickerFormItem} from 'src/view/shared/form/items/DatePickerFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import Message from 'src/view/shared/message';
import UserAutocompleteFormItem from 'src/view/user/autocomplete/UserAutocompleteFormItem';

function TareaReassignTasksModal(props) {
  const dispatch = useDispatch();
  const tareaRows = useSelector(tareaListSelectors.selectRows);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [ejecutor, setEjecutor] = useState<any>(null);
  const [editFechaProgramacion, setEditFechaProgramacion] = useState(false);
  const [editFechaLimite, setEditFechaLimite] = useState(false);
  const [editPrioridad, setEditPrioridad] = useState(false);
  const [editSubtitulo, setEditSubtitulo] = useState(false);

  const doReassign = async () => {
    try {
      setReassignLoading(true);
      const values = form.getValues();
      const fechaProgramacion = editFechaProgramacion ? values.fechaProgramacion : undefined;
      const fechaLimite = editFechaLimite ? values.fechaLimite : undefined;
      const prioridad = editPrioridad ? values.prioridad : undefined;
      const subtitulo = editSubtitulo ? values.subtitulo : undefined;
      const response = await TareaService.reassignTasks(
        props.ids,
        ejecutor || undefined,
        fechaProgramacion,
        fechaLimite,
        prioridad,
        subtitulo,
      );
      if (response?.status === 200) {
        if (props.drawerId === 'TareaFormPage') {
          dispatch({
            type: layoutActions.DRAWER_VISIBLE,
            payload: {
              drawerId: 'TareaFormPage',
              drawerVisible: true,
              drawerChanged: moment().unix(),
              drawerUpdateComponent: 'TareaFormPage',
              recordId: props.ids[0],
            },
          });
        } else {
          dispatch(tareaListActions.doFetchCurrentFilter());
        }
        Message.success(response?.message);
        doClose();
      } else {
        Message.error(response?.message);
      }
    } catch (error) {
      Errors.handle(error);
    } finally {
      setReassignLoading(false);
    }
  };

  const doClose = () => {
    return props.onClose();
  };

  const recursiveFindTarea = (rows, id) => {
    let tarea: any = null;
    for (let row of rows) {
      if (row.id === id) {
        tarea = row;
      } else if (row.children) {
        tarea = recursiveFindTarea(row.children, id);
      }
      if (tarea) break;
    }
    return tarea;
  };
  const getTareaLabel = (id) => {
    const tarea = recursiveFindTarea(tareaRows, id);
    let label = tarea?.titulo || '';
    label += ' ' + tarea?.modeloTarea?.titulo || '';
    return label;
  };

  const form = useForm({
    defaultValues: {
      fechaProgramacion: moment().format('YYYY-MM-DD'),
      fechaLimite: moment().format('YYYY-MM-DD'),
      prioridad: '',
      subtitulo: '',
    },
  });

  return ReactDOM.createPortal(
    <Dialog open={true} onClose={doClose} maxWidth="md" fullWidth={true}>
      <DialogTitle
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <h2 style={{margin: 0}}>{i18n('common.reassignTasksToANewExecutor')}</h2>
        <IconButton onClick={doClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{minHeight: '50vh'}}>
        <FormProvider {...form}>
          <form>
            <div style={{marginBottom: '16px'}}>
              <h3>{i18n('common.executor')}</h3>
              <p>{i18n('common.selectTheNewExecutorForTheSelectedTasks')}</p>
              <UserAutocompleteFormItem
                name="ejecutor"
                label={i18n('common.executor')}
                required={false}
                showCreate={false}
                autoFocus={true}
                onChange={(value) => setEjecutor(value?.id)}
                value={ejecutor}
                menuPortalTarget={document.body}
              />
            </div>

            <div style={{marginBottom: '8px'}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFechaProgramacion}
                    onChange={(e) => setEditFechaProgramacion(e.target.checked)}
                  />
                }
                label={i18n('common.editFechaInicio')}
              />
              {editFechaProgramacion && (
                <div style={{paddingLeft: 32}}>
                  <DatePickerFormItem
                    name="fechaProgramacion"
                    label={i18n('common.fechaInicio')}
                    required={false}
                  />
                </div>
              )}
            </div>

            <div style={{marginBottom: '8px'}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFechaLimite}
                    onChange={(e) => setEditFechaLimite(e.target.checked)}
                  />
                }
                label={i18n('entities.tarea.fields.fechaLimite')}
              />
              {editFechaLimite && (
                <div style={{paddingLeft: 32}}>
                  <DatePickerFormItem
                    name="fechaLimite"
                    label={i18n('entities.tarea.fields.fechaLimite')}
                    required={false}
                  />
                </div>
              )}
            </div>

            <div style={{marginBottom: '8px'}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editPrioridad}
                    onChange={(e) => setEditPrioridad(e.target.checked)}
                  />
                }
                label={i18n('entities.tarea.fields.prioridad')}
              />
              {editPrioridad && (
                <div style={{paddingLeft: 32}}>
                  <SelectFormItem
                    name="prioridad"
                    label={i18n('entities.tarea.fields.prioridad')}
                    options={['Baja', 'Media', 'Alta'].map((val) => ({
                      value: val,
                      label: val,
                    }))}
                    required={false}
                  />
                </div>
              )}
            </div>

            <div style={{marginBottom: '16px'}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editSubtitulo}
                    onChange={(e) => setEditSubtitulo(e.target.checked)}
                  />
                }
                label={i18n('common.editSubtitulo')}
              />
              {editSubtitulo && (
                <div style={{paddingLeft: 32}}>
                  <InputFormItem
                    name="subtitulo"
                    label={i18n('entities.tarea.fields.subtitulo')}
                    required={false}
                  />
                </div>
              )}
            </div>

            <div style={{backgroundColor: 'ghostwhite', borderRadius: 10, padding: 10, marginTop: 20, marginBottom: 16}}>
              <h3>{i18n('common.tasks')}</h3>
              <p>{i18n('common.theFollowingTasksWillBeReassigned')}:</p>
              <ul>
                {props.ids.map((id) => (
                  <li key={id}>{getTareaLabel(id)}</li>
                ))}
              </ul>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={
            reassignLoading ||
            (!ejecutor && !editFechaProgramacion && !editFechaLimite && !editPrioridad && !editSubtitulo)
          }
          onClick={doReassign}
          color="primary"
          variant={'contained'}>
          {i18n('common.reassign')}
        </Button>
        <Button onClick={doClose} color="secondary">
          {i18n('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>,
    (document as any).getElementById('modal-root'),
  );
}

export default TareaReassignTasksModal;
